import { createCRUDHook } from './createCRUDHook';
import {
  PartRequestDto,
  JsonPatch,
  FilterOptionModel,
  PartRequestCommunicationDto,
  PartRequestChangeLogDto,
  PartRequestImagesDto,
} from 'utils/models';
import {
  FILTERED_ENTITIES,
  TBL_PART_REQUEST,
  TBL_PART_REQUEST_CHANGE_LOG,
  TBL_PART_REQUEST_COMMUNICATION,
  TBL_PART_REQUEST_IMAGES,
} from './apiEndPoints';
import { AxiosResponse } from 'axios';
import { prepareQueryParams } from 'utils';

interface PartRequestCustomOperations {
  getPartRequestCommunicationData: () => Promise<PartRequestCommunicationDto[]>;
  getPartRequestCommunicationDataByFE: (
    filteredEntities: FilterOptionModel
  ) => Promise<PartRequestCommunicationDto[]>;
  addPartRequestCommunicationData: (
    param: PartRequestCommunicationDto
  ) => Promise<PartRequestCommunicationDto>;
  updatePartRequestCommunicationData: (
    param: JsonPatch[],
    id: number
  ) => Promise<AxiosResponse>;
  deletePartRequestCommunicationData: (id: number) => Promise<boolean>;

  getPartRequestChangeLogDataByFE: (
    filteredEntities: FilterOptionModel
  ) => Promise<PartRequestChangeLogDto[]>;
  addPartRequestChangeLogData: (
    param: PartRequestChangeLogDto
  ) => Promise<PartRequestChangeLogDto>;

  // Images
  getPartRequestImagesData: () => Promise<PartRequestImagesDto[]>;
  getPartRequestImagesDataById: (id: number) => Promise<PartRequestImagesDto>;
  getPartRequestImagesDataByFE: (
    filteredEntities: FilterOptionModel
  ) => Promise<PartRequestImagesDto[]>;
  addPartRequestImagesData: (
    param: PartRequestImagesDto
  ) => Promise<PartRequestImagesDto>;
  updatePartRequestImagesData: (
    param: JsonPatch[],
    id: number
  ) => Promise<AxiosResponse>;
  deletePartRequestImagesData: (id: number) => Promise<boolean>;
}

const { Context, useManager, useConsumer } = createCRUDHook<
  PartRequestDto,
  PartRequestDto[],
  PartRequestCustomOperations
>({
  entityName: 'PartRequest',
  endpoint: TBL_PART_REQUEST,

  customOperations: (auth) => ({
    getPartRequestCommunicationData: async () => {
      const res = await auth.makeAuthenticatedGet(
        TBL_PART_REQUEST_COMMUNICATION
      );
      return res.data;
    },

    getPartRequestCommunicationDataByFE: async (filteredEntities) => {
      const res = await auth.makeAuthenticatedGet(
        TBL_PART_REQUEST_COMMUNICATION +
          FILTERED_ENTITIES +
          `?${prepareQueryParams(filteredEntities)}`
      );
      return res.data;
    },

    addPartRequestCommunicationData: async (param) => {
      const res = await auth.makeAuthenticatedPost(
        TBL_PART_REQUEST_COMMUNICATION,
        param
      );
      return res.data;
    },

    updatePartRequestCommunicationData: async (param, id) => {
      return auth.makeAuthenticatedPatch(
        `${TBL_PART_REQUEST_COMMUNICATION}/${id}`,
        param
      );
    },

    deletePartRequestCommunicationData: async (id) => {
      const res = await auth.makeAuthenticatedDelete(
        `${TBL_PART_REQUEST_COMMUNICATION}/${id}`
      );
      return res.data;
    },

    getPartRequestChangeLogDataByFE: async (filteredEntities) => {
      const res = await auth.makeAuthenticatedGet(
        TBL_PART_REQUEST_CHANGE_LOG +
          FILTERED_ENTITIES +
          `?${prepareQueryParams(filteredEntities)}`
      );
      return res.data;
    },

    addPartRequestChangeLogData: async (param) => {
      const res = await auth.makeAuthenticatedPost(
        TBL_PART_REQUEST_CHANGE_LOG,
        param
      );
      return res.data;
    },

    getPartRequestImagesData: async () => {
      const res = await auth.makeAuthenticatedGet(TBL_PART_REQUEST_IMAGES);
      return res.data;
    },

    getPartRequestImagesDataById: async (id) => {
      const res = await auth.makeAuthenticatedGet(
        `${TBL_PART_REQUEST_IMAGES}/${id}`
      );
      return res.data;
    },

    getPartRequestImagesDataByFE: async (filteredEntities) => {
      const res = await auth.makeAuthenticatedGet(
        TBL_PART_REQUEST_IMAGES +
          FILTERED_ENTITIES +
          `?${prepareQueryParams(filteredEntities)}`
      );
      return res.data;
    },

    addPartRequestImagesData: async (param) => {
      const res = await auth.makeAuthenticatedPost(
        TBL_PART_REQUEST_IMAGES,
        param
      );
      return res.data;
    },

    updatePartRequestImagesData: async (param, id) => {
      return auth.makeAuthenticatedPatch(
        `${TBL_PART_REQUEST_IMAGES}/${id}`,
        param
      );
    },

    deletePartRequestImagesData: async (id) => {
      const res = await auth.makeAuthenticatedDelete(
        `${TBL_PART_REQUEST_IMAGES}/${id}`
      );
      return res.data;
    },
  }),
});

export const PartRequestContext = Context;
export const usePartRequestManager = useManager;
export const usePartRequest = useConsumer;
